import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, MoreVertical } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { useToast } from '../../hooks/use-toast';

const AdminPageCategoriesList = () => {
  const { toast } = useToast();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const [categories, setCategories] = useState([]);
  const [enabledCountries, setEnabledCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [expandedAccordions, setExpandedAccordions] = useState([]);

  const [formData, setFormData] = useState({
    slug: '',
    order: 0,
    isActive: true,
    versions: []
  });

  const getFlagEmoji = (countryCode) => {
    if (!countryCode) return '🌍';
    const codePoints = countryCode.toUpperCase().split('').map(char => 127397 + char.charCodeAt());
    return String.fromCodePoint(...codePoints);
  };

  const generateSlug = (text) => {
    return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  };

  useEffect(() => {
    fetchEnabledCountries();
    fetchCategories();
  }, []);

  const fetchEnabledCountries = async () => {
    try {
      const response = await fetch(`${API_URL}/api/localization/countries`);
      const data = await response.json();
      setEnabledCountries(data);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/api/page-categories/admin/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      // Ensure data is always an array
      setCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]); // Ensure categories is always an array on error
      toast({
        title: 'Error',
        description: 'Failed to load page categories',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const initializeFormForCreate = () => {
    const versions = enabledCountries.map(country => ({
      countryCode: country.code,
      language: country.language,
      name: '',
      isAvailable: true
    }));

    setFormData({
      slug: '',
      order: 0,
      isActive: true,
      versions: versions
    });
    setExpandedAccordions([]);
    setEditingCategory(null);
  };

  const initializeFormForEdit = (category) => {
    // Merge existing versions with newly enabled countries
    const existingVersions = category.versions || [];
    const mergedVersions = enabledCountries.map(country => {
      const existingVersion = existingVersions.find(v => v.countryCode === country.code);
      
      if (existingVersion) {
        return existingVersion;
      } else {
        return {
          countryCode: country.code,
          language: country.language,
          name: '',
          description: '',
          isAvailable: true
        };
      }
    });
    
    setFormData({
      slug: category.slug || '',
      order: category.order || 0,
      isActive: category.isActive !== undefined ? category.isActive : true,
      versions: mergedVersions
    });
    setExpandedAccordions([]);
    setEditingCategory(category);
  };

  const handleOpenDialog = (category = null) => {
    if (category) {
      initializeFormForEdit(category);
    } else {
      initializeFormForCreate();
    }
    setIsDialogOpen(true);
  };

  const updateVersionField = (versionIndex, field, value) => {
    const newVersions = [...formData.versions];
    newVersions[versionIndex] = {
      ...newVersions[versionIndex],
      [field]: value
    };
    setFormData({ ...formData, versions: newVersions });
  };

  const handleSave = async () => {
    try {
      if (!formData.slug) {
        toast({
          title: 'Validation Error',
          description: 'Slug is required',
          variant: 'destructive',
        });
        return;
      }

      const hasValidVersion = formData.versions.some(v => v.name && v.name.trim() !== '');
      if (!hasValidVersion) {
        toast({
          title: 'Validation Error',
          description: 'At least one country version with a name is required',
          variant: 'destructive',
        });
        return;
      }

      const token = localStorage.getItem('accessToken');
      let response;

      if (editingCategory) {
        const categoryId = editingCategory.id || editingCategory._id;
        response = await fetch(`${API_URL}/api/page-categories/${categoryId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });
      } else {
        response = await fetch(`${API_URL}/api/page-categories`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save page category');
      }

      toast({
        title: 'Success',
        description: editingCategory ? 'Page category updated successfully' : 'Page category created successfully',
      });

      setIsDialogOpen(false);
      fetchCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save page category',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (category) => {
    if (!window.confirm('Are you sure you want to delete this page category?')) return;

    try {
      const token = localStorage.getItem('accessToken');
      const categoryId = category.id || category._id;
      
      const response = await fetch(`${API_URL}/api/page-categories/${categoryId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete category');
      }

      toast({
        title: 'Success',
        description: 'Page category deleted successfully',
      });

      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete page category',
        variant: 'destructive',
      });
    }
  };

  const getCategoryDisplayName = (category) => {
    return category.versions?.[0]?.name || category.slug || 'Unnamed Category';
  };

  const getCountryFlags = (category) => {
    if (!category.versions || category.versions.length === 0) return '—';
    return category.versions.filter(v => v.isAvailable).map(v => getFlagEmoji(v.countryCode)).join(' ');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#be1f1a]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Page Categories</h1>
          <p className="text-gray-600 mt-1">{categories.length} categories found</p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="bg-[#be1f1a] hover:bg-[#a71b17]">
          <Plus className="h-4 w-4 mr-2" />
          Add Page Category
        </Button>
      </div>

      {/* Categories Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Order</TableHead>
              <TableHead>Countries</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!categories || categories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12 text-gray-500">
                  No page categories found
                </TableCell>
              </TableRow>
            ) : (
              Array.isArray(categories) && categories.map((category) => (
                <TableRow key={category.id || category._id}>
                  <TableCell className="font-medium">{getCategoryDisplayName(category)}</TableCell>
                  <TableCell className="font-mono text-sm">{category.slug}</TableCell>
                  <TableCell>{category.order}</TableCell>
                  <TableCell className="text-lg">{getCountryFlags(category)}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      category.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {category.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleOpenDialog(category)} className="cursor-pointer">
                          <Edit className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDelete(category)} className="text-red-600 cursor-pointer">
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCategory ? 'Edit Page Category' : 'Add Page Category'}</DialogTitle>
            <DialogDescription>
              {editingCategory ? 'Update category details and country versions' : 'Create a new page category with country-specific versions'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Master Fields */}
            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold mb-4">Master Information</h3>
              
              <div className="space-y-4">
                <div>
                  <Label>Slug (URL) *</Label>
                  <Input
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    placeholder="category-name"
                  />
                </div>

                <div>
                  <Label>Display Order</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    checked={formData.isActive}
                    onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                  />
                  <Label className="font-normal cursor-pointer">Active Category</Label>
                </div>
              </div>
            </div>

            {/* Country Versions */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Country Versions</h3>
              <p className="text-sm text-gray-600 mb-4">At least one version with a name is required.</p>

              <Accordion type="multiple" value={expandedAccordions} onValueChange={setExpandedAccordions}>
                {formData.versions.map((version, versionIndex) => {
                  const country = enabledCountries.find(c => c.code === version.countryCode);
                  if (!country) return null;

                  return (
                    <AccordionItem key={versionIndex} value={`country-${versionIndex}`} className="border rounded-lg mb-2">
                      <AccordionTrigger className="px-4 hover:no-underline">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{getFlagEmoji(country.code)}</span>
                          <div className="text-left">
                            <div className="font-semibold">{country.name}</div>
                            <div className="text-sm text-gray-500">
                              {country.languageName}
                              {version.name && <span className="ml-2 text-blue-600">• {version.name}</span>}
                            </div>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pb-4">
                        <div className="space-y-4 pt-4">
                          <div>
                            <Label>Category Name ({country.languageName}) *</Label>
                            <Input
                              value={version.name}
                              onChange={(e) => {
                                updateVersionField(versionIndex, 'name', e.target.value);
                                if (!editingCategory && versionIndex === 0 && !formData.slug) {
                                  setFormData({ ...formData, slug: generateSlug(e.target.value) });
                                }
                              }}
                              placeholder={`Enter category name in ${country.languageName}`}
                            />
                          </div>

                          <div className="flex items-center space-x-2">
                            <Checkbox
                              checked={version.isAvailable}
                              onCheckedChange={(checked) => updateVersionField(versionIndex, 'isAvailable', checked)}
                            />
                            <Label className="font-normal cursor-pointer">
                              ✓ Available in {country.name}
                            </Label>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} className="bg-[#be1f1a] hover:bg-[#a71b17]">
              {editingCategory ? 'Update' : 'Create'} Category
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPageCategoriesList;
